import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';

const PIM_MAP = {
  Double: 240,
  Minor: 120,
  Major: 300,
  Misconduct: 600,
}

export function generateInitialHeartbeat() {
  return {
    delay: 1000,
    nextFn: runPeriod,
    speed: 10,
  }
}

/*
**************************
**** LOGIC FUNCTIONS *****
**************************
*/
/**
 * Runs through a normal / overtime period
 * @param {object} data - The live data 
 * @param {object} heartbeat - Heartbeat settings
 * @param {object} periods - The amount of periods in the game
 */
function runPeriod(data, heartbeat, periods) {
  // Defaults to return
  let updData = cloneDeep(data);
  let nextFn = runPeriod;
  let delay = 1000;

  // Get the target time to work with
  let target = setTargetTime(updData.clock, heartbeat.speed);

  // Get events for this time period, and apply them
  const { events, intervention } = getEventsForTimePeriod(data.remainingEvents[data.period.key], target);
  updData.remainingEvents[data.period.key] = removeEventsFromRemainingList(data.remainingEvents[data.period.key], events);
  updData.pastEvents = [...events.reverse(), ...updData.pastEvents];

  // Take events, manipulate stats, score, penalties, etc
  updData = handleEvents(updData, events.reverse());

  // If we have a new intervention (an event triggered an immediate stop)
  // Set the new target here
  if (intervention) {
    target = intervention.target;
    delay = intervention.delay;
  }

  // Set clock
  updData.clock.penalties = updatePenaltyTimes(updData.clock.penalties, target, updData.period.key, periods);
  updData.clock.elapsed = target;

  // If period is over change the nextFn
  if (updData.clock.elapsed === updData.clock.total) {
    nextFn = runPeriodChange;
  }

  // End game if goal was scored in OT
  if(isOvertimeGoalScored(updData.period, events)){
    nextFn = runEndGame;
  }

  // Send it
  return [
    updData,
    { ...heartbeat, nextFn, delay }
  ]
}

/**
 * Changes the period over to the next one
 * Runs correct nextFn depending on if it's a new period
 * shootout or the game has ended
 * @param {object} data - The live data 
 * @param {object} heartbeat - Heartbeat settings
 * @param {object} periods - The amount of periods in the game
 */
function runPeriodChange(data, heartbeat, periods) {
  // Clone the data
  const updData = cloneDeep(data);

  // Heartbeat defaults
  let nextFn = runPeriod;

  // Get next period
  const period = determineNextPeriod(updData.period, periods);

  if (!period) {
    nextFn = runEndGame;
  } else {
    updData.period = period;
    updData.clock = {
      ...updData.clock,
      elapsed: 0,
      total: period.length
    };

    // If shootout
    if (period.isShootout) {
      nextFn = runShootout;
    } else {
      updData.goals[period.key] = [];
      updData.penalties[period.key] = [];
    }
  }

  // Send it
  return [
    updData,
    { ...heartbeat, nextFn, delay: 2000 }
  ]
}

/**
 * Runs through the shootout
 * @param {object} data - The live data 
 * @param {object} heartbeat - Heartbeat settings
 * @param {object} periods - The amount of periods in the game
 */
function runShootout(data, heartbeat) {
  // Clone the data
  const updData = cloneDeep(data);
  const shootoutData = updData.remainingEvents.shootout[1].events;
  let nextFn = runShootout;

  // Shootout not initialized
  if (!updData.shootout.isActive) {
    updData.shootout.isActive = true;

    // Send it
    return [
      updData,
      { ...heartbeat, nextFn, delay: 2000 }
    ]
  }

  // No shootout data left, end the game
  if (!shootoutData.length) {
    updData.shootout = removePendingAttempts(updData.shootout);

    // Send it
    return [
      updData,
      { ...heartbeat, nextFn: runEndGame, delay: 1 }
    ]
  }

  // Shootout continues, remove latest event
  // And add it to the past events
  const nextShot = shootoutData.shift();
  updData.pastEvents.unshift(nextShot);
  updData.shootout = simulateShootoutAttempt(updData.shootout, nextShot, updData.stats.skaters);

  // Send it
  return [
    updData,
    { ...heartbeat, nextFn, delay: 1500 }
  ]
}


/**
 * Sets the game to final state
 * Stops the interval
 * @param {object} data - The live data 
 * @param {object} heartbeat - Heartbeat settings
 * @param {object} periods - The amount of periods in the game
 */
function runEndGame(data, heartbeat) {
  // Clone the data
  const updData = cloneDeep(data);

  updData.period = {
    isFinal: true,
    key: 'final',
    name: determineFinalText(updData.period),
    number: 0,
    length: 0
  };

  updData.clock = {
    ...updData.clock,
    elapsed: 0,
    total: 0
  };

  // Send it
  return [
    updData,
    { ...heartbeat, nextFn: null, delay: null }
  ]
}


/*
**************************
**** CLOCK FUNCTIONS *****
**************************
*/
/*
** This will give us the base time we want to
** get to, before any goals, etc are scored
** which could cut this short.
*/
function setTargetTime(clock, speed) {
  let target = clock.elapsed + speed;

  // Set it to the length of the period
  // if time goes over
  if (target > clock.total) {
    target = clock.total;
  }

  return target;
}

/*
** This will give us the base time we want to
** get to, before any goals, etc are scored
** which could cut this short.
*/
function determineNextPeriod(currentPeriod, periods) {
  const periodsArr = Object.values(periods);
  const idx = periodsArr.findIndex(period => currentPeriod.key === period.key);
  return periodsArr[idx + 1];
}

function determineFinalText(period) {
  const base = 'Final';

  if (period.isShootout) {
    return `${base} (SO)`;
  }

  if (period.isOT) {
    return `${base} (OT)`;
  }

  return base;
}

/**
 * Will go through all the penalties, and update time remaining from them.
 * @param {object} penalties - Penalties on each side
 * @param {number} clock - Current state of period clock
 * @param {string} currentPeriod - Current period key
 * @param {object} periods - All periods object
 */
function updatePenaltyTimes(penalties, clock, currentPeriod, periods) {
  for (const side in penalties) {
    penalties[side] = penalties[side]
      .map(penalty => ({
        ...penalty,
        remaining: determinePenaltyRemainingTime(penalty, clock, currentPeriod, periods),
      }))
      .filter(penalty => penalty.remaining > 0);
  }

  return penalties;
}

/**
 * Calculated remaining time for the penalty given
 * @param {object} penalty - Penalty to calculate
 * @param {number} clock - Current state of period clock
 * @param {string} currentPeriod - Current period key
 * @param {object} periods - All periods object
 */
function determinePenaltyRemainingTime(penalty, clock, currentPeriod, periods){
  // If we are in a different period than the penalty began,
  // add the period length to the equation.
  if(currentPeriod !== penalty.period){
    const penaltyPeriodLen = periods[penalty.period].length;
    return PIM_MAP[penalty.type] - (penaltyPeriodLen - penalty.startTime) - clock
  }

  // Same period, do regular calc
  return PIM_MAP[penalty.type] - (clock - penalty.startTime)
}

/**
 * After a PP goal, will correctly remove the penalty off the board
 * @param {object} goal - The goal object
 * @param {object} penalties - The current state of the penalties
 */
function removePenaltyAfterPPGoal(goal, penalties) {
  const { second: goalTime, period, result: { team: scoringTeam } } = goal;
  const scoredOnTeam = Object.keys(penalties).find(key => key !== scoringTeam);

  // Get times of scoring team penalties (for co-insiding penalties)
  const scoringTeamMinorPenalties = penalties[scoringTeam]
    .filter(penalty => ['Minor', 'Double'].includes(penalty.type))
    .map(penalty => penalty.startTime);

  // Get penalties eligible for removal
  const eligiblePenalties = penalties[scoredOnTeam]
    // Only keep minor or double minor penalties
    .filter(penalty => ['Minor', 'Double'].includes(penalty.type))
    // Only keep penalties that are in range of a goal
    // (If sim speed is too fast, penalties might have already expired)
    .filter(penalty => {
      if (penalty.period !== period) {
        // Can only happen in full length periods right?!
        return PIM_MAP[penalty.type] - (1200 - penalty.startTime) > goalTime;
      }

      return penalty.startTime > goalTime - PIM_MAP[penalty.type];
    })
    // Take away co-insiding penalties
    .filter(penalty => !scoringTeamMinorPenalties.includes(penalty.startTime));

  if(!eligiblePenalties.length) {
    return penalties;
  }

  const index = penalties[scoredOnTeam].findIndex(penalty => penalty.startTime === eligiblePenalties[0].startTime);

  // Double minor just put clock to 2:00 remaining
  // TODO: what about funky situations? 4 on 3 goal? 5 on 3 with double minor 2:35 remaining? who comes out.
  if(penalties[scoredOnTeam][index].type === "Double" && penalties[scoredOnTeam][index].remaining > 120) {
    penalties[scoredOnTeam][index].remaining = 120;
  } else {
    penalties[scoredOnTeam].splice(index, 1);
  }

  return penalties;
}

/*
**************************
***** EVENT FUNCTIONS ****
**************************
*/

/**
 * Checks to see if we are in an overtime period, and a goal is scored
 * @param {object} period - Current period data
 * @param {array} events - New events about to be added to the data
 * @returns {boolean}
 */
function isOvertimeGoalScored(period, events){
  // Not in OT.. no need to check anything else
  if (!period.isOT){
    return false;
  }

  for(let event of events){
    // Result type is goal, return true
    if(get(event, 'result.type', null) === 'goal'){
      return true;
    }
  }

  // No goals
  return false;
}

/**
 * Given a list of events, and a target time, will find all the events that should 'happen' this round of simulation.
 * @param {object} events - Object of events where the key is the second the event happened (in the period)
 * @param {*} target - The max time we want to hit, before an intervention
 * @returns {array} - The list of events that have happend this simulation round
 */
function getEventsForTimePeriod(eventObj, target) {
  const events = [];
  let intervention = undefined;

  for (let second in eventObj) {
    // Past the time, end it
    if (second > target) {
      break;
    }

    // If we have an intervention, end the loop
    if (intervention) {
      return { events, intervention }
    }

    // Get time of event
    const timeEvts = eventObj[second];
    const time = { period: timeEvts.time.key, second: timeEvts.time.time };

    // Loop over the events, adding them to the events array
    for (const event of timeEvts.events) {
      // Add to events array
      events.push({
        ...time,
        ...event
      });

      // A goal was scored (allowed or disallowed)
      // Add intervention (to break the loop and return immediately)
      // NOTE: don't return here because sometimes goalies are changed 
      // Which happens AFTER the goal is scored.
      if ((event.result && event.result.type === 'goal') || event.review) {
        intervention = { target: parseInt(second, 10), delay: 5000 };
      }
    }
  }

  // No intervention, send the events
  return { events, intervention };
}

/**
 * Given the remaining events, and the 'next' events,
 * this will remove the next events from the remaining list.
 * @param {object} obj - The list of remaining events
 * @param {array} toRemove - The list of events to remove
 */
function removeEventsFromRemainingList(obj, toRemove) {
  for (const event of toRemove) {
    delete obj[event.second];
  }

  return obj;
}

/**
 * Will take a list of events, and update the data object as needed
 * Adding stats to players, penalties, goals, assists, etc
 * @param {object} data - The data object in its entirety
 * @param {array} events - The events in this timeframe
 */
function handleEvents(data, events) {
  for (let event of events) {
    // Faceoff
    if (event.event === 'faceoff') {
      handleFaceoffEvent(data, event);
      continue;
    }

    // Goalie Replaced
    if (event.event === 'goalieReplaced') {
      handleGoalieReplacedEvent(data, event);
      continue;
    }

    // Goalie Pulled
    if (event.event === 'goaliePulled') {
      handleGoaliePulledEvent(data, event);
      continue;
    }

    // Goalie Returned
    if (event.event === 'goalieReturned') {
      handleGoalieReturnedEvent(data, event);
      continue;
    }

    // Hit
    if (event.event === 'hit') {
      handleHitEvent(data, event);
      continue;
    }

    // Injury
    if (event.event === 'injury') {
      handleInjuryEvent(data, event);
      continue;
    }

    // Penalty
    if (event.event === 'penalty') {
      handlePenaltyEvent(data, event);
      continue;
    }

    if (event.event === 'ejected') {
      handleEjectedEvent(data, event);
      continue;
    }


    // Shot
    if (event.event === 'shot') {
      // Reviewed, no goal
      if (event.review && event.review.outcome === 'goalReviewNoGood') {
        handleGoalReviewNoGood(data, event);
        continue;
      }

      // Don't end, let the other events pick up the result
      if (event.review && event.review.outcome === 'goalReviewGood') {
        handleGoalReviewGood(data, event);
      }

      // Goal
      if (event.result.type === 'goal') {
        handleGoalEvent(data, event);
        continue;
      }

      // Shot Blocked
      if (event.result.type === 'shotBlocked') {
        handleShotBlockedEvent(data, event);
        continue;
      }

      // Shot saved
      if (event.result.type === 'shotSaved') {
        handleShotSavedEvent(data, event);
        continue;
      }

      // Shot missed, Shot Hit Post
      // No manipulation neccessary
      if (['shotMissed', 'shotHitPost'].includes(event.result.type)) {
        continue;
      }

      debugger;
    }

    // Other, no manipulation needed
    if (['fight', 'icing', 'offside'].includes(event.event)) {
      continue;
    }

    debugger;
  }


  return data;
}

/**
 * Will handle the goalieReplaced event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goalieReplaced event
 */
function handleGoalieReplacedEvent(data, event) {
  const { player: goalieId } = event;
  const goalie = data.stats.goalies.find(goalie => goalie.id === goalieId);

  // Set goalie to be 'in goal'
  data.inGoal[goalie.team] = goalie.id;

  // Show goalies stats
  goalie.isHidden = false;
}

/**
 * Will handle the goaliePulled event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goaliePulled event
 */
function handleGoaliePulledEvent(data, event) {
  const { player: goalieId } = event;
  const goalie = data.stats.goalies.find(goalie => goalie.id === goalieId);

  // Set no goalie for this team
  data.inGoal[goalie.team] = null;
}

/**
 * Will handle the goalieReturned event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goalieReturned event
 */
function handleGoalieReturnedEvent(data, event) {
  const { player: goalieId } = event;
  const goalie = data.stats.goalies.find(goalie => goalie.id === goalieId);

  // Set the goalie for this team
  data.inGoal[goalie.team] = goalie.id;
}

/**
 * Will handle the faceoff event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The faceoff event
 */
function handleFaceoffEvent(data, event) {
  const { winner: winnerId, loser: loserId } = event;

  // Player adjustments
  const winner = data.stats.skaters.find(skater => skater.id === winnerId);
  winner.faceoffsWon += 1;
  winner.faceoffsTaken += 1;

  const loser = data.stats.skaters.find(skater => skater.id === loserId);
  loser.faceoffsTaken += 1;

  // Team adjustments
  data.stats.teams[winner.team].faceoffWins += 1;
}

/**
 * Will handle adding a note for goals that went to a review, and were called back
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goalReviewNoGood event
 */
function handleGoalReviewNoGood(data, event) {
  const { player: shooterId, deflect: { player: deflectorId } = {} } = event;
  const shooter = data.stats.skaters.find(skater => {
    if(deflectorId){
      return skater.id === deflectorId;
    }

    return skater.id === shooterId;
  });

  // Add the note
  data.notes.push({
    type: 'Goal Review - Goal Disallowed',
    description: `After review, ${shooter.team.charAt(0).toUpperCase() + shooter.team.slice(1)} goal by ${shooter.name} has been called back!`
  });

  // Adjustments
  shooter.shots += 1;
  data.stats.teams[shooter.team].shots.total += 1;
}

/**
 * Will handle adding a note for goals that went to a review and stood
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goalReviewGood event
 */
function handleGoalReviewGood(data, event) {
  const { player: shooterId } = event;
  const shooter = data.stats.skaters.find(skater => skater.id === shooterId);

  // Just add it to the notes
  data.notes.push({
    type: 'Goal Review - Good Goal',
    description: `After review, ${shooter.team.charAt(0).toUpperCase() + shooter.team.slice(1)} goal by ${shooter.name} has been confirmed!`
  });
}

/**
 * Will handle the goal event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The goal event
 */
function handleGoalEvent(data, event) {
  const { player: scorerId, deflect: { player: deflectorId } = {} } = event;

  const scorer = data.stats.skaters.find(skater => {
    if(deflectorId){
      return skater.id === deflectorId;
    }

    return skater.id === scorerId;
  });
  const gAllowTeamId = Object.keys(data.stats.teams).find(key => key !== scorer.team);

  // Goal Scorer adjustments
  scorer.goals += 1;
  scorer.shots += 1;
  scorer.points += 1;

  // Assist adjustments
  for (const { id: assistId } of event.result.assists) {
    const assister = data.stats.skaters.find(skater => skater.id === assistId);

    // Sometimes a goalie will get an assist, and we don't track that
    if (assister) {
      assister.assists += 1;
      assister.points += 1;
    }
  }

  // Goalie adjustments
  if (event.result.variant !== 'Empty Net') {
    const goalie = data.stats.goalies.find(goalie => goalie.id === data.inGoal[gAllowTeamId]);
    goalie.shots += 1;
  }

  // Team adjustments
  data.stats.teams[scorer.team].shots.total += 1;
  data.stats.teams[scorer.team].goals.total += 1;

  // PP Goal
  if (event.result.variant === 'PP') {
    data.stats.teams[scorer.team].pp.goals += 1;
    data.stats.teams[scorer.team].pp.attempts += 1;
    data.stats.teams[gAllowTeamId].pk.attempts += 1;

    // TODO: Take away penalty if it was a PP goal
    data.clock.penalties = removePenaltyAfterPPGoal(event, data.clock.penalties);
  }

  // Add goal to goals branch
  data.goals[data.period.key].push(event);
}

/**
 * Will handle the hit event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The hit event
 */
function handleHitEvent(data, event) {
  const { winner: winnerId } = event;

  // Player adjustments
  const winner = data.stats.skaters.find(skater => skater.id === winnerId);
  winner.hits += 1;

  // TODO: Add hits taken??

  // Team adjustments
  data.stats.teams[winner.team].hits += 1;
}

/**
 * Will handle the injury event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The injury event
 */
function handleInjuryEvent(data, event) {
  // Add the note
  data.notes.push({
    type: 'Injury',
    description: event.description
  });
}

/**
 * Will handle the penalty event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The penalty event
 */
function handlePenaltyEvent(data, event) {
  const pim = PIM_MAP[event.type] / 60;

  const { player: playerId } = event;
  const player = [...data.stats.skaters, ...data.stats.goalies].find(player => player.id === playerId);

  // TODO: Goalie?? Not tracking PIM yet
  if (player.position !== 'G') {
    player.pims += pim;
  }

  // Add to clock, not fighting majors though
  if (event.penalty !== 'Fighting') {
    data.clock.penalties[player.team].push({
      period: event.period,
      player: event.player,
      penalty: event.penalty,
      startTime: event.second,
      type: event.type,
      remaining: PIM_MAP[event.type]
    });
  }

  // Add to team stats
  data.stats.teams[player.team].pims += pim;

  // Add penalty to penalties branch
  data.penalties[data.period.key].push(event);
}

/**
 * Will handle the ejected event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The ejected event
 */
function handleEjectedEvent(data, event) {
  const { player: playerId } = event;
  const player = [...data.stats.skaters, ...data.stats.goalies].find(player => player.id === playerId);
  const pim = PIM_MAP.Misconduct / 60;

  // TODO: Goalie?? Not tracking PIM yet
  if (player.position !== 'G') {
    player.pims += pim;
  }

  // Add to team stats
  data.stats.teams[player.team].pims += pim;

  // Add to notes
  data.notes.push({
    type: 'Ejection',
    description: event.description
  });

  // Add penalty to penalties branch
  data.penalties[data.period.key].push({
    ...event,
    type: 'Game Misconduct',
    penalty: 'Ejection'
  });
}

/**
 * Will handle the shotSaved event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The shotSaved event
 */
function handleShotSavedEvent(data, event) {
  const { player: shooterId, deflect: { player: deflectorId } = {}, result: { player: goalieId } } = event;

  // Player adjustments
  const shooter = data.stats.skaters.find(skater => {
    if(deflectorId){
      return skater.id === deflectorId;
    }

    return skater.id === shooterId;
  });
  shooter.shots += 1;

  const goalie = data.stats.goalies.find(goalie => goalie.id === goalieId);
  goalie.saves += 1;
  goalie.shots += 1;

  // Team adjustments
  data.stats.teams[shooter.team].shots.total += 1;
}

/**
 * Will handle the shotBlocked event
 * @param {object} data - The data object in its entirety
 * @param {object} event - The shotBlocked event
 */
function handleShotBlockedEvent(data, event) {
  const { result: { player: blockerId } } = event;

  // Player adjustments
  const blocker = data.stats.skaters.find(skater => skater.id === blockerId);
  blocker.shotsBlocked += 1;

  // TODO: Track shooter shots blocked?

  // Team adjustments
  data.stats.teams[blocker.team].blockedShots += 1;
}

/*
**************************
*** SHOOTOUT FUNCTIONS ***
**************************
*/
/**
 * Will add the shootout attempt into the array
 * of shootout attempts for his team
 * @param {object} shootout - The current state of the shootout
 * @param {object} attempt - The next shootout attempt event
 * @param {array} skaters - The list of all skaters in the game
 */
function simulateShootoutAttempt(shootout, attempt, skaters) {
  // Find team of shooter
  const { team } = skaters.find(skater => skater.id === attempt.player);

  // Create shootout obj
  const result = {
    id: attempt.player,
    player: attempt.player,
    result: attempt.result.type === 'goal' ? attempt.result.type : 'miss',
  }

  // Find latest pending event for this team
  const index = shootout[team].findIndex(shot => shot.result === 'pending');

  // If no pending, we must be in sudden death shootout
  // For this team, just add to the end of the array.
  if (index === -1) {
    shootout[team].push(result);

    // Add a pending to the other team
    const otherTeam = Object.keys(shootout).find(key => ![team, 'isActive', 'round'].includes(key));
    shootout[otherTeam].push({
      player: '',
      result: 'pending',
    });

    return shootout;
  }

  // Replace the pending index with the shot
  shootout[team][index] = result;
  return shootout;
}

/**
 * Will remove all shootout attempts (on both sides)
 * That are result === pending
 * @param {object} shootout - The current state of the shootout
 */
function removePendingAttempts(shootout) {
  // Get team keys
  const keys = Object.keys(shootout).filter(key => key !== 'isActive');

  // Remove the ones that are pending
  keys.forEach(key => {
    shootout[key] = shootout[key]
      .filter(attempt => attempt.result !== 'pending');
  });

  return shootout;
}


export default {
  generateInitialHeartbeat,
  runPeriod,
}